import React, { useState } from 'react'
import { fmtShortDay, money } from '../financial/formatting'

// Revenue per New York day for the last 14 days, today last. revenue_daily
// carries every day, so an empty one is a real zero. Each bar stacks our
// share (bottom) under partners' (top); the exact pair is in the tooltip,
// by mouse or keyboard.
export default function RevenueDailyChart({ rows = [] }) {
  const [active, setActive] = useState(null)
  const days = rows.map((r) => {
    const ours = Number(r.our_share || 0)
    const partners = Number(r.partners_share || 0)
    return { date: r.date, ours, partners, total: ours + partners }
  })
  const max = Math.max(0, ...days.map((d) => d.total))
  const last = days.length - 1
  const h = (v) => (max > 0 ? (Math.max(0, v) / max) * 90 : 0)

  return (
    <section className="card" aria-labelledby="hm-daily-title" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="hm-card-head" style={{ flexWrap: 'wrap' }}>
        <h2 id="hm-daily-title" style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Revenue, last 14 days</h2>
        <div aria-hidden="true" style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-secondary)' }}>
          <LegendItem color="var(--accent)">Our share</LegendItem>
          <LegendItem color="var(--share-partner)">Partners share</LegendItem>
        </div>
      </div>
      {max === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>No revenue in the last 14 days.</p>
      ) : (
        <div>
          <div className="hm-day-bars" role="list" aria-label="Revenue per day">
            {days.map((d, i) => (
              <div
                key={d.date}
                role="listitem"
                tabIndex={0}
                className="hm-day"
                aria-label={`${fmtShortDay(d.date)}: our share ${money(d.ours)}, partners share ${money(d.partners)}`}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                style={{
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', minWidth: 0,
                  cursor: 'default', borderRadius: 4, background: active === i ? 'var(--chart-cursor)' : 'transparent',
                }}
              >
                <div style={{ height: `${h(d.partners)}%`, background: 'var(--share-partner)', borderRadius: '4px 4px 0 0' }} />
                <div style={{ height: `${h(d.ours)}%`, minHeight: d.total > 0 ? 2 : 0, background: 'var(--accent)', borderRadius: d.partners > 0 ? 0 : '4px 4px 0 0' }} />
              </div>
            ))}
            {active !== null && <DayTooltip day={days[active]} index={active} count={days.length} barPct={h(days[active].total)} />}
          </div>
          <div className="hm-day-labels" aria-hidden="true">
            {days.map((d, i) => (
              <span
                key={d.date}
                className={(last - i) % 2 === 1 ? 'hm-day-label-odd' : undefined}
                style={{ fontSize: 10, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', color: i === last ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: i === last ? 700 : 400 }}
              >
                {fmtShortDay(d.date)}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

function LegendItem({ color, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
      {children}
    </span>
  )
}

// Above the hovered bar; anchored to the bar's outer side near the edges so
// it never spills out of the card.
function DayTooltip({ day, index, count, barPct }) {
  const placement = index < 2
    ? { left: `${(index / count) * 100}%`, transform: 'none' }
    : index > count - 3
      ? { left: `${((index + 1) / count) * 100}%`, transform: 'translateX(-100%)' }
      : { left: `${((index + 0.5) / count) * 100}%`, transform: 'translateX(-50%)' }
  return (
    <div
      role="tooltip"
      style={{
        position: 'absolute', bottom: `calc(${barPct}% + 8px)`, zIndex: 2, pointerEvents: 'none',
        padding: '6px 9px', borderRadius: 6, whiteSpace: 'nowrap', fontSize: 12,
        background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow)',
        ...placement,
      }}
    >
      <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{fmtShortDay(day.date)}</div>
      <div style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>Our share: <strong>{money(day.ours)}</strong></div>
      <div style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>Partners share: <strong>{money(day.partners)}</strong></div>
    </div>
  )
}
